
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { Clock, Scissors, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';

interface Service {
  id: string;
  name: string;
  duration: number;
  price: number;
  category: string;
  description: string;
  popular?: boolean;
}

interface StudioServicesProps {
  studioId: string;  // This is already correctly typed as string
  onBookService: (serviceId: string) => void;
}

// Mock service data - in a real implementation you would fetch this from your Supabase backend
const MOCK_SERVICES: Service[] = [
  {
    id: 's1',
    name: 'Klassischer Haarschnitt',
    duration: 30,
    price: 29.99,
    category: 'Haarschnitt',
    description: 'Waschen, Schneiden, Föhnen für alle Haarlängen.',
    popular: true
  },
  {
    id: 's2',
    name: 'Farbe & Pflege',
    duration: 90,
    price: 79.99,
    category: 'Coloration',
    description: 'Komplette Haarfärbung mit anschließender Pflegebehandlung.'
  },
  {
    id: 's3',
    name: 'Bartpflege',
    duration: 20,
    price: 19.99,
    category: 'Bart',
    description: 'Konturenschnitt und Pflege für deinen Bart.'
  },
  {
    id: 's4',
    name: 'Komplett-Styling',
    duration: 60,
    price: 49.99,
    category: 'Styling',
    description: 'Waschen, Schneiden, Styling und Beratung.',
    popular: true
  }
];

export const StudioServices: React.FC<StudioServicesProps> = ({ studioId, onBookService }) => {
  
  // Group services by category
  const servicesByCategory = MOCK_SERVICES.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {} as Record<string, Service[]>);

  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Dienstleistungen</h2>
      
      {Object.entries(servicesByCategory).map(([category, services]) => (
        <div key={category} className="space-y-4">
          <h3 className="text-xl font-medium text-zinc-300">{category}</h3>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {services.map((service) => (
              <motion.div key={service.id} variants={itemVariants}>
                <Card className="bg-card/50 backdrop-blur-sm hover:bg-card/80 transition-all duration-200">
                  <CardContent className="p-4">
                    <div className="flex flex-col gap-2">
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{service.name}</h4>
                            {service.popular && (
                              <Badge variant="secondary" className="bg-chrona-accent text-white">
                                <Sparkles size={12} className="mr-1" /> Beliebt
                              </Badge>
                            )}
                          </div>
                          
                          <p className="text-sm text-zinc-400">{service.description}</p>
                        </div>
                        
                        <div className="text-lg font-semibold">
                          {service.price.toLocaleString('de-DE', {
                            style: 'currency',
                            currency: 'EUR'
                          })}
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-1 text-sm text-zinc-400">
                          <Clock size={14} />
                          <span>{service.duration} Min.</span>
                        </div>
                        
                        <Button
                          size="sm"
                          onClick={() => onBookService(service.id)}
                          className="gap-1"
                        >
                          <Scissors size={14} />
                          Termin buchen
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      ))}
    </div>
  );
};
